/**
 * Time hub — รวมทุกอย่างที่เกี่ยวกับ "เวลาทำงาน" ของพนักงาน.
 *
 * Tabs:
 *   - ตั้งค่าเวลา → render time-scoped fields จาก /owner/settings (reuse
 *                  FIELD_GROUPS) — เปลี่ยนค่าใช้ useUpdateSettings เดียวกัน
 *   - กะ          → HRShiftsContent
 *   - เข้างานวันนี้ → HRAttendanceTodayContent
 *   - วันหยุด     → HRHolidaysContent
 *   - วันลา       → HRLeavePackagesContent
 *
 * URL hash (#settings|#shifts|#today|#holidays|#leave) preserves deep links.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { HubHeader, PillTabs, roleLabelTh } from '../../components/layout/HubHeader';
import { Loading } from '../../components/layout/Loading';
import { useAuthStore } from '../../lib/store';
import { useSettings, useUpdateSettings } from '../../hooks';
import { useToast } from '../../components/ui/toast';
import { Save } from 'lucide-react';
import { FIELD_GROUPS, Field } from '../owner/Settings';
import { HRShiftsContent } from './Shifts';
import { HRRosterCalendarContent } from './RosterCalendar';
import { HRAttendanceTodayContent } from './AttendanceToday';
import { HRHolidaysContent } from './Holidays';
import { HRLeavePackagesContent } from './LeavePackages';

type TimeTab = 'settings' | 'shifts' | 'calendar' | 'today' | 'holidays' | 'leave';
const VALID_TABS: TimeTab[] = ['settings', 'shifts', 'calendar', 'today', 'holidays', 'leave'];

/** Time-scoped subset of /owner/settings — same hooks, just filtered to
 *  `tab: 'time'` field groups so the user can adjust สูตรเวลา in-place. */
function TimeSettingsContent() {
  const { data, isLoading } = useSettings();
  const update = useUpdateSettings();
  const toast = useToast();
  const [form, setForm] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  if (isLoading || !data) return <Loading />;

  function setField(key: string, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  }
  async function handleSave() {
    try {
      await update.mutateAsync(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  const groups = FIELD_GROUPS.filter((g) => g.tab === 'time');
  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <div key={g.title} className="card p-4">
          <div className="font-semibold mb-3">{g.title}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {g.fields.map((f) => (
              <Field key={f.key} f={f} value={form[f.key] ?? ''} onChange={(v) => setField(f.key, v)} />
            ))}
          </div>
        </div>
      ))}
      <div className="flex items-center gap-3">
        <button onClick={handleSave} disabled={update.isPending} className="btn-primary">
          <Save className="w-4 h-4" />
          {update.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
        </button>
        {saved && <span className="text-sm text-green-600">บันทึกแล้ว ✓</span>}
      </div>
    </div>
  );
}

export function Time() {
  const [tab, setTab] = useState<TimeTab>(() => {
    const h = (typeof window !== 'undefined' ? window.location.hash.replace('#', '') : '') as TimeTab;
    return VALID_TABS.includes(h) ? h : 'today';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const next = `#${tab}`;
    if (window.location.hash !== next) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search + next);
    }
  }, [tab]);
  // sync tab จาก #hash เมื่อ navigate ผ่าน sidebar (เมนูย่อย)
  const __loc = useLocation();
  useEffect(() => {
    const h = __loc.hash.replace('#', '') as TimeTab;
    if (VALID_TABS.includes(h)) setTab(h);
  }, [__loc.hash]);


  const role = useAuthStore((s) => s.profile?.role ?? 'EMPLOYEE');
  // หัวหน้างาน (SUPERVISOR) เห็นเฉพาะแท็บจัดการเวรทีม — ซ่อนการตั้งค่าระดับบริษัท
  // (วันหยุด/สิทธิ์การลา/เวลา) ที่เป็นงานของ HR/เจ้าของ.
  const isSupervisor = role === 'SUPERVISOR';
  const TIME_TABS: Array<{ key: TimeTab; label: string }> = isSupervisor
    ? [
        { key: 'today', label: 'เข้างานวันนี้' },
        { key: 'shifts', label: 'กะ' },
        { key: 'calendar', label: 'ปฏิทินเวรทีม' },
      ]
    : [
        { key: 'today', label: 'เข้างานวันนี้' },
        { key: 'shifts', label: 'กะ' },
        { key: 'calendar', label: 'ปฏิทินเวรทีม' },
        { key: 'holidays', label: 'ตั้งค่าวันหยุด' },
        { key: 'leave', label: 'ตั้งค่าสิทธิ์การลา' },
        { key: 'settings', label: 'ตั้งค่าเวลา' },
      ];
  // ถ้า supervisor เปิด #hash ของแท็บที่ซ่อน → เด้งกลับ today (กันเข้าถึงเนื้อหา).
  const allowedTab = TIME_TABS.some((t) => t.key === tab) ? tab : 'today';

  return (
    <AdminLayout title="เวลาทำงาน">
      <HubHeader
        section="เวลาทำงาน · ลา · OT"
        title={TIME_TABS.find((t) => t.key === allowedTab)?.label ?? 'เวลาทำงาน'}
        roleLabel={roleLabelTh(role)}
      />
      <PillTabs tabs={TIME_TABS} active={allowedTab} onChange={setTab} />

      {allowedTab === 'settings' && <TimeSettingsContent />}
      {allowedTab === 'shifts' && <HRShiftsContent />}
      {allowedTab === 'calendar' && <HRRosterCalendarContent />}
      {allowedTab === 'today' && <HRAttendanceTodayContent />}
      {allowedTab === 'holidays' && <HRHolidaysContent />}
      {allowedTab === 'leave' && <HRLeavePackagesContent />}
    </AdminLayout>
  );
}
